import type {
  ComponentProps,
  ComponentType,
  FC,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  ReactNode,
  RefAttributes,
} from "react";
import React, { createContext, forwardRef, useContext } from "react";

import { useBrand } from "../brand";
import type { Brands } from "../brand/types";
import { useCurrencyFormat } from "../currency/CurrencyFormatProvider";
import { useCurrencyLocale } from "../currency/CurrencyLocaleProvider";
import { useDateLocale } from "../date/DateLocaleProvider";
import { ErrorBoundary, ErrorNamespaceProvider } from "../error_handling";
import { DatameshNamespaceProvider } from "../html_event";
import TranslationsNamespaceProvider from "../i18n/TranslationsNamespaceProvider";
import { useThemeVersion } from "../theme/ThemeVersionProvider";
import type { PrismaVersionType } from "../theme/types";
import type { CurrencyFormat } from "../types";

export const aliases = [];

const NamespaceContext = createContext<string>("");

export const useNamespace = () => useContext(NamespaceContext);
interface DateFnsLocale {
  code: string;
}

interface PrismaProviderProps {
  brand: Brands;
  version: PrismaVersionType;
  currencyLocale: {
    locale: string;
    minimumFractionDigits?: number;
    numberLocale?: string;
  };
  currencyFormat?: CurrencyFormat;
  dateFnsLocale: DateFnsLocale;
  children: ReactNode;
}

export const withModuleContext = ({
  namespace,
  PrismaProvider,
}: {
  namespace: string;
  PrismaProvider: FC<PrismaProviderProps>;
}) => {
  // No explicit type argument — infer props from the real component.
  function wrap<TComponent extends ComponentType<any>, TRef = unknown>(
    Component: TComponent,
  ): ForwardRefExoticComponent<
    ComponentProps<TComponent> & RefAttributes<TRef>
  >;
  // Explicit `withModuleContext<Props>(Component)` — Component must satisfy Props.
  function wrap<TProps, TRef = unknown>(
    Component: ComponentType<TProps>,
  ): ForwardRefExoticComponent<TProps & RefAttributes<TRef>>;
  // Implementation, shared by both overloads above.
  function wrap<TProps, TRef = unknown>(Component: ComponentType<TProps>) {
    const Inner = Component as ComponentType<any>;
    return forwardRef<TRef, PropsWithoutRef<TProps>>((props, ref) => {
      const brand = useBrand();
      const currencyLocale = useCurrencyLocale();
      const currencyFormat = useCurrencyFormat();
      const dateLocale = useDateLocale();
      const version = useThemeVersion();
      return (
        <PrismaProvider
          brand={brand}
          version={version}
          currencyLocale={currencyLocale}
          dateFnsLocale={dateLocale}
          currencyFormat={currencyFormat}
        >
          <NamespaceContext.Provider value={namespace}>
            <DatameshNamespaceProvider namespace={namespace}>
              <TranslationsNamespaceProvider namespace={namespace}>
                <ErrorNamespaceProvider namespace={namespace}>
                  <ErrorBoundary name="default_library_error">
                    <Inner {...props} ref={ref} />
                  </ErrorBoundary>
                </ErrorNamespaceProvider>
              </TranslationsNamespaceProvider>
            </DatameshNamespaceProvider>
          </NamespaceContext.Provider>
        </PrismaProvider>
      );
    });
  }

  return wrap;
};
